// @systeme: interface
// Les notifications côté navigateur : demander la permission, abonner cet appareil,
// le désabonner. Tout ce qui est décidé (quand, quoi) l'est par le serveur.
import { api } from './api';

export type EtatPermission = 'indisponible' | 'a_demander' | 'accordee' | 'refusee';

export function etatPermission(): EtatPermission {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  )
    return 'indisponible';
  if (Notification.permission === 'granted') return 'accordee';
  if (Notification.permission === 'denied') return 'refusee';
  return 'a_demander';
}

/** Sur iPhone, les notifications n'existent que si l'app est installée sur l'écran d'accueil. */
export function estInstallee(): boolean {
  if (typeof window === 'undefined') return false;
  // Safari sur iPhone expose « navigator.standalone », hors standard : on le lit sans forcer le type.
  const standalone: unknown = Reflect.get(navigator, 'standalone');
  return window.matchMedia('(display-mode: standalone)').matches || standalone === true;
}

function versOctets(clePublique: string): Uint8Array<ArrayBuffer> {
  const base64 = (clePublique + '='.repeat((4 - (clePublique.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const brut = atob(base64);
  const octets = new Uint8Array(new ArrayBuffer(brut.length));
  for (let i = 0; i < brut.length; i += 1) octets[i] = brut.charCodeAt(i);
  return octets;
}

export async function abonnementActuel(): Promise<PushSubscription | null> {
  if (etatPermission() === 'indisponible') return null;
  const enregistrement = await navigator.serviceWorker.ready;
  return enregistrement.pushManager.getSubscription();
}

/** Demande la permission si besoin, abonne l'appareil, l'enregistre côté serveur. */
export async function abonnerCetAppareil(clePublique: string): Promise<boolean> {
  if (etatPermission() === 'indisponible') return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const enregistrement = await navigator.serviceWorker.ready;
  const abonnement =
    (await enregistrement.pushManager.getSubscription()) ??
    (await enregistrement.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: versOctets(clePublique),
    }));
  const json = abonnement.toJSON();
  if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) return false;
  await api.notifications.abonner({
    endpoint: json.endpoint,
    cles: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
    agent: navigator.userAgent.slice(0, 300),
  });
  return true;
}

export async function desabonnerCetAppareil(): Promise<void> {
  const abonnement = await abonnementActuel();
  if (!abonnement) return;
  await api.notifications.desabonner(abonnement.endpoint);
  await abonnement.unsubscribe();
}
